import { useState } from "react";
import { Check, Copy, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { streamAssistant } from "@/lib/aiStream";

/**
 * One-click assistant note. Nothing is generated until the officer asks for it.
 * kind: "project" | "deepdive" | "report"
 */
export const AiNote = ({ kind, params, cta, blurb, testId }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const write = async () => {
    setBusy(true);
    setText("");
    try {
      await streamAssistant("/assistant/insight", { kind, ...params }, (piece) =>
        setText((prev) => prev + piece),
      );
    } catch (err) {
      toast.error(err.message);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied.");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="border border-border bg-card" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(199_89%_48%)]" />
          <div>
            <div className="label-caps">Assistant</div>
            {blurb && <p className="mt-0.5 text-[11px] text-muted-foreground">{blurb}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {text && !busy && (
            <button
              type="button"
              data-testid={`${testId}-copy`}
              onClick={copy}
              className="flex items-center gap-1.5 border border-border px-3 py-2 font-data text-[10px] uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            data-testid={`${testId}-generate`}
            onClick={write}
            className="flex items-center gap-1.5 border border-border bg-foreground px-3 py-2 font-data text-[10px] uppercase tracking-widest text-background transition-opacity duration-200 hover:opacity-85 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : text ? (
              <RefreshCw className="h-3 w-3" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            {busy ? "Writing" : text ? "Write again" : cta}
          </button>
        </div>
      </div>

      {(text || busy) && (
        <div className="px-4 py-4">
          <p
            className="whitespace-pre-wrap text-sm leading-relaxed"
            data-testid={`${testId}-text`}
          >
            {text}
            {busy && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-primary" />}
          </p>
          {text && !busy && (
            <p className="font-data mt-4 border-t border-border pt-3 text-[10px] uppercase tracking-wider text-muted-foreground">
              Drafted from this portal's own register · please check the figures before
              you circulate it
            </p>
          )}
        </div>
      )}
    </div>
  );
};
